// ============================================================================
// FSA Engagement Signature Panel Pane — FSA-2.3 Signature Workflow
// ============================================================================
// Per-engagement signature panel editor. Lives as a tab inside
// FsaEngagementScreen. Reads + writes via
// /api/v1/FsaEngagementSignaturePanels.
//
// Layout:
//   • Header — Audit Firm picker + Place + Report Date + Carry-Forward action
//   • Auditor side — partner list (add / remove rows, UDIN per row)
//   • Board side   — director list (add / remove rows, scoped to client +
//                    engagement date via the master picker's eligibleAsOf filter)
//   • Save button — POST /upsert with snapshots resolved server-side. A
//                    signatory already on the panel keeps the name it was saved
//                    with; "Refresh names from masters" re-copies them.
//
// ADR-0024 — director picks follow the engagement's own directors list
// (Corporate Facts) order, and an empty panel can be filled from it.
//
// UDIN validation runs on save — 18-char ICAI format. Soft-validates per
// row so the auditor can fix without losing the row.

import { toDateInputValue, todayIsoLocal } from 'utils/dateUtils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import {
  useFsaEngagementSignaturePanel,
  useFsaSignaturePanelCarryForward,
} from 'hooks/api/fsa/useFsaSignature';
import {
  fsaEngagementSignaturePanelService,
  fsaAuditFirmService,
  fsaAuditPartnerService,
  fsaDirectorSignatoryService,
} from 'api/services/fsa/fsaSignatureService';
import { fsaPartnerService } from 'api/services/fsa/fsaPartnerService';
import { fsaCorporateFactsService } from 'api/services/fsa/fsaCorporateFactsService';
import { parseCorporateFactsData } from 'hooks/api/fsa/useFsaCorporateFacts';
import type { CorporateFactsDirector } from 'api/types/fsa/corporateFacts.types';
import { fromMaster, inOffice, moveEntry } from 'components/modules/fsa/engagement/corporate-facts/factsDirectors';
import {
  UDIN_EXAMPLE,
  UDIN_FORMAT_MESSAGE,
  UDIN_REGEX,
  type UpsertEngagementSignaturePanelSignatoryItem,
  type SignatoryRole,
  type SignatoryMasterType,
} from 'api/types/fsa/signature.types';

interface FsaSignaturePanelPaneProps {
  engagementId: string;
  /** Used as eligibleAsOf filter when picking directors (defaults to today). */
  clientId?: string | null;
  /**
   * Engagement template code. ICAI Non-Corporate Entity templates
   * (IN_ICAI_NCE_*) have no board — the governance side is the partners,
   * so the board-side UI relabels Director → Partner. Underlying role
   * value stays "DIRECTOR" (the board side); only the labels change.
   */
  templateCode?: string | null;
}

interface DraftSignatory {
  /** Local-only key so React can track rows without server IDs yet. */
  key: string;
  role: SignatoryRole;
  signatoryMasterId: string;
  /**
   * Which master the picked id came from. Carried explicitly because role
   * alone cannot say: a partnership firm's partners and a company's
   * directors both print on the entity side under role DIRECTOR, but live
   * in fsa.tbl_partner and fsa.tbl_director_signatory respectively.
   */
  signatoryMasterType: SignatoryMasterType;
  /** Cached label so the dropdown still shows the picked option. */
  signatoryLabel: string;
  /** Cached snapshot fields shown read-only after pick (preview only). */
  designationPreview?: string;
  dinOrMembershipPreview?: string;
  /** For partners: per-engagement UDIN. */
  udinNumber: string;
  /** For ordering. */
  displayOrder: number;
}

const newDraft = (
  role: SignatoryRole,
  displayOrder: number,
  signatoryMasterType: SignatoryMasterType,
): DraftSignatory => ({
  key: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  signatoryMasterId: '',
  signatoryMasterType,
  signatoryLabel: '',
  udinNumber: '',
  displayOrder,
});

// A DATE column read as its date part — timezone-safe (see utils/dateUtils).
const toIsoDate = toDateInputValue;

const FsaSignaturePanelPane: React.FC<FsaSignaturePanelPaneProps> = ({ engagementId, clientId, templateCode }) => {
  // ICAI Non-Corporate Entity templates → relabel the board side for the
  // owners. Role value stays DIRECTOR under the hood (the DB CHECK is
  // AUDITOR_PARTNER / DIRECTOR / OTHER); only the wording differs.
  //
  // The owner noun is per-template, not shared across all NCE templates: a
  // proprietary concern has one owner and no partners, so labelling its
  // signatory "Partner" is simply wrong. Matched on the specific suffix
  // rather than added to the generic NCE branch, so a future NCE template
  // (HUF, AOP) falls back to the neutral default until it declares its own.
  const upperTemplateCode = (templateCode ?? '').toUpperCase();
  const isNonCorporate = upperTemplateCode.startsWith('IN_ICAI_NCE');
  const isProprietorship = upperTemplateCode.startsWith('IN_ICAI_NCE_PROP');
  // An LLP's accounts are signed by its designated partners (LLP Act, 2008
  // s.7), each identified by a DPIN — the print shows "DPIN: …" under the name.
  const isLlp = upperTemplateCode.startsWith('IN_ICAI_NCE_LLP');
  const boardNoun = isProprietorship ? 'Proprietor' : isLlp ? 'Designated Partner' : isNonCorporate ? 'Partner' : 'Director';
  // WHICH MASTER the entity-side picker reads. A non-corporate entity's
  // signatories are its own partners/proprietor (fsa.tbl_partner, the
  // FSA → Partners master); a company's are its directors
  // (fsa.tbl_director_signatory). Both print under role DIRECTOR, so the
  // role cannot carry this and the panel sends it explicitly.
  const ownerMasterType: SignatoryMasterType = isNonCorporate ? 'PARTNER' : 'DIRECTOR';
  const ownerSideHeading = isProprietorship
    ? 'Owner’s Side — Proprietor'
    : isLlp
      ? 'Owners’ Side — Designated Partners'
      : isNonCorporate
      ? 'Owners’ Side — Partners'
      : 'Board Side — Directors / Officers';
  const { data: panel, loading, refetch } = useFsaEngagementSignaturePanel(engagementId);
  const { load: loadCarryForward, loading: cfLoading } = useFsaSignaturePanelCarryForward();

  // ───── form state ─────
  const [auditFirmId, setAuditFirmId] = useState('');
  const [auditFirmLabel, setAuditFirmLabel] = useState('');
  const [place, setPlace] = useState('');
  const [reportDate, setReportDate] = useState('');
  // The entity side signs at its own place and date (tables/118). Blank place
  // prints no Place line there; blank date means the auditor's report date.
  const [entityPlace, setEntityPlace] = useState('');
  const [entityDate, setEntityDate] = useState('');
  // Directors / partners eligible to sign are those in office on the date the
  // entity side signs.
  const entitySignDate = entityDate || reportDate;
  const [notes, setNotes] = useState('');
  const [auditors, setAuditors] = useState<DraftSignatory[]>([]);
  const [directors, setDirectors] = useState<DraftSignatory[]>([]);
  const [rowVersion, setRowVersion] = useState(0);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [refreshFromMasters, setRefreshFromMasters] = useState(false);

  // ADR-0024 — the engagement's own directors list (Corporate Facts), in its
  // order; the master's list for the year until Corporate Facts has saved one.
  const [roster, setRoster] = useState<CorporateFactsDirector[]>([]);
  useEffect(() => {
    if (!clientId || isNonCorporate) { setRoster([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const facts = await fsaCorporateFactsService.get(engagementId);
        const saved = facts.data ? parseCorporateFactsData(facts.data.data).directors : undefined;
        const list = saved ?? (await fsaCorporateFactsService.getMasterDirectors(engagementId)).map(fromMaster);
        if (!cancelled) setRoster(list);
      } catch {
        if (!cancelled) setRoster([]);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, engagementId, isNonCorporate]);

  // Hydrate form from saved panel (on first load + on refetch).
  useEffect(() => {
    if (!panel) {
      // No panel yet — leave defaults empty for first save.
      setAuditFirmId('');
      setAuditFirmLabel('');
      setPlace('');
      setReportDate('');
      setEntityPlace('');
      setEntityDate('');
      setNotes('');
      setAuditors([]);
      setDirectors([]);
      setRowVersion(0);
      return;
    }
    setAuditFirmId(String(panel.auditFirmId));
    setAuditFirmLabel(
      panel.auditFirmName
        ? `${panel.auditFirmName}${panel.auditFirmRegistrationNumber ? ' (' + panel.auditFirmRegistrationNumber + ')' : ''}`
        : '',
    );
    setPlace(panel.place ?? '');
    setReportDate(toIsoDate(panel.reportDate));
    setEntityPlace(panel.entityPlace ?? '');
    setEntityDate(toIsoDate(panel.entityDate));
    setNotes(panel.notes ?? '');
    setRowVersion(panel.rowVersion);

    const ap: DraftSignatory[] = [];
    const dir: DraftSignatory[] = [];
    panel.signatories.forEach((s, i) => {
      const draft: DraftSignatory = {
        key: `srv-${s.id}`,
        role: s.role,
        signatoryMasterId: String(s.signatoryMasterId ?? ''),
        // Saved rows carry their own type. Rows written before the column
        // existed come back null; mirror the API's fallback so a re-save
        // doesn't silently retype them.
        signatoryMasterType: s.signatoryMasterType
          ?? (s.role === 'AUDITOR_PARTNER' ? 'AUDIT_PARTNER' : 'DIRECTOR'),
        signatoryLabel: `${s.nameSnapshot}${s.designationSnapshot ? ' · ' + s.designationSnapshot : ''}`,
        designationPreview: s.designationSnapshot,
        dinOrMembershipPreview: s.dinOrMembershipSnapshot ?? '',
        udinNumber: s.udinNumber ?? '',
        displayOrder: s.displayOrder || i + 1,
      };
      if (s.role === 'AUDITOR_PARTNER') ap.push(draft);
      else dir.push(draft);
    });
    setAuditors(ap.sort((a, b) => a.displayOrder - b.displayOrder));
    setDirectors(dir.sort((a, b) => a.displayOrder - b.displayOrder));
  }, [panel]);

  // ───── async option loaders ─────
  const loadAuditFirmOptions = useCallback(async (search: string) => {
    const resp = await fsaAuditFirmService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined, includeInactive: false });
    return resp.data.map(f => ({
      value: String(f.id),
      label: `${f.firmName} (${f.firmRegistrationNumber})`,
    }));
  }, []);

  const loadPartnerOptions = useCallback(async (search: string) => {
    if (!auditFirmId) return [];
    const resp = await fsaAuditPartnerService.getAll({
      page: 1, pageSize: 50,
      firmId: auditFirmId,
      searchTerm: search || undefined,
      includeInactive: false,
    });
    return resp.data.map(p => ({
      value: String(p.id),
      label: `${p.partnerName} (${p.membershipNumber}) · ${p.designation}`,
      // Cache extras for snapshot preview (read in onChange via second source-of-truth lookup)
      meta: {
        designation: p.designation,
        membership: p.membershipNumber,
      },
    }));
  }, [auditFirmId]);

  const loadDirectorOptions = useCallback(async (search: string) => {
    if (!clientId) return [];
    const eligibleAsOf = entitySignDate || todayIsoLocal();
    const resp = await fsaDirectorSignatoryService.getAll({
      page: 1, pageSize: 50,
      clientId,
      eligibleAsOf,
      searchTerm: search || undefined,
      includeInactive: false,
    });
    // In the engagement's directors-list order; anyone not in it after.
    const rank = (id: string) => {
      const i = roster.findIndex(r => r.director_id.toLowerCase() === id.toLowerCase());
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    const ordered = [...resp.data].sort((a, b) => rank(String(a.id)) - rank(String(b.id)));
    return ordered.map(d => ({
      value: String(d.id),
      label: `${d.signatoryName}${d.din ? ' (DIN: ' + d.din + ')' : ''} · ${d.designation}`,
      meta: {
        designation: d.designation,
        din: d.din ?? '',
      },
    }));
  }, [clientId, entitySignDate, roster]);

  /**
   * Entity-side options for a NON-CORPORATE template — the client's own
   * partners / proprietor from fsa.tbl_partner (FSA → Partners).
   *
   * This is NOT fsaAuditPartnerService: that master holds the AUDIT FIRM's
   * signing partners and feeds the auditor side. Confusing the two is what
   * made this list come back empty for partnership engagements — the picker
   * queried directors, and a partnership client has no director records.
   *
   * signatoriesOnly filters to partners actually nominated to sign
   * (is_signatory); effective dates are filtered server-side against the
   * report date, same as directors, so a retired partner drops out of new
   * engagements while staying on previously-signed FS via the snapshot.
   */
  const loadOwnerPartnerOptions = useCallback(async (search: string) => {
    if (!clientId) return [];
    const eligibleAsOf = entitySignDate || todayIsoLocal();
    const resp = await fsaPartnerService.getAll({
      page: 1, pageSize: 50,
      clientId,
      eligibleAsOf,
      signatoriesOnly: true,
      searchTerm: search || undefined,
      includeInactive: false,
    });
    return resp.data.map(p => ({
      value: String(p.id),
      label: `${p.partnerName}${p.dpin ? ' (DPIN: ' + p.dpin + ')' : ''} · ${p.designation}`,
      meta: {
        // The identifier line is the DPIN (LLP designated partners), never the
        // PAN: a DIN / DPIN is conventionally printed beneath the name on the
        // face of the financial statements, a PAN is not.
        designation: p.designation,
        din: p.dpin ?? '',
      },
    }));
  }, [clientId, entitySignDate]);

  const loadOwnerOptions = isNonCorporate ? loadOwnerPartnerOptions : loadDirectorOptions;

  // ───── row editing ─────
  const updateAuditor = useCallback((key: string, patch: Partial<DraftSignatory>) => {
    setAuditors(prev => prev.map(s => s.key === key ? { ...s, ...patch } : s));
  }, []);
  const removeAuditor = useCallback((key: string) => {
    setAuditors(prev => prev.filter(s => s.key !== key));
  }, []);
  const addAuditor = useCallback(() => {
    setAuditors(prev => [...prev, newDraft('AUDITOR_PARTNER', prev.length + 1, 'AUDIT_PARTNER')]);
  }, []);

  const updateDirector = useCallback((key: string, patch: Partial<DraftSignatory>) => {
    setDirectors(prev => prev.map(s => s.key === key ? { ...s, ...patch } : s));
  }, []);
  const removeDirector = useCallback((key: string) => {
    setDirectors(prev => prev.filter(s => s.key !== key));
  }, []);
  // Role stays DIRECTOR for every entity-side signatory (that is the column
  // whitelist, and it is what the print renderer groups on); the MASTER the
  // row is picked from is what varies by template.
  const addDirector = useCallback(() => {
    setDirectors(prev => [...prev, newDraft('DIRECTOR', prev.length + 1, ownerMasterType)]);
  }, [ownerMasterType]);
  const moveDirector = useCallback((index: number, delta: -1 | 1) => {
    setDirectors(prev => moveEntry(prev, index, delta));
  }, []);
  const moveAuditor = useCallback((index: number, delta: -1 | 1) => {
    setAuditors(prev => moveEntry(prev, index, delta));
  }, []);
  // ADR-0024 — fill an empty board side from the engagement's directors list,
  // in its order: everyone in office on the report date (today when unset).
  const rosterSigners = useMemo(() => {
    const asOf = entitySignDate || todayIsoLocal();
    return roster.filter(d => inOffice(d, asOf));
  }, [roster, entitySignDate]);
  const addFromCorporateFacts = useCallback(() => {
    setDirectors(rosterSigners.map((d, i) => ({
      ...newDraft('DIRECTOR', i + 1, 'DIRECTOR'),
      signatoryMasterId: d.director_id,
      signatoryLabel: `${d.name}${d.din ? ' (DIN: ' + d.din + ')' : ''}${d.designation ? ' · ' + d.designation : ''}`,
      designationPreview: d.designation ?? '',
      dinOrMembershipPreview: d.din ?? '',
    })));
  }, [rosterSigners]);

  // ───── carry-forward ─────
  const [carryForwardConfirmOpen, setCarryForwardConfirmOpen] = useState(false);
  const handleCarryForwardClick = useCallback(() => {
    setCarryForwardConfirmOpen(true);
  }, []);
  const handleCarryForwardConfirm = useCallback(async () => {
    setCarryForwardConfirmOpen(false);
    const draft = await loadCarryForward(engagementId);
    if (!draft) {
      notificationService.toast.info('No prior-year panel available to carry forward.');
      return;
    }
    // Apply draft to form.
    setAuditFirmId(String(draft.auditFirmId));
    // Firm label will refresh on next save read; for now show ID.
    setAuditFirmLabel('');
    setPlace('');
    setReportDate('');
    setEntityPlace('');
    setEntityDate('');
    setNotes('');
    const ap: DraftSignatory[] = [];
    const dir: DraftSignatory[] = [];
    (draft.signatories ?? []).forEach((s, i) => {
      const d: DraftSignatory = {
        key: `cf-${i}-${Math.random().toString(36).slice(2, 6)}`,
        role: s.role,
        signatoryMasterId: String(s.signatoryMasterId ?? ''),
        signatoryMasterType: s.signatoryMasterType
          ?? (s.role === 'AUDITOR_PARTNER' ? 'AUDIT_PARTNER' : 'DIRECTOR'),
        signatoryLabel: s.nameOverride ?? '',
        designationPreview: s.designationOverride ?? '',
        dinOrMembershipPreview: s.dinOrMembershipOverride ?? '',
        udinNumber: s.udinNumber ?? '',
        displayOrder: s.displayOrder || i + 1,
      };
      if (s.role === 'AUDITOR_PARTNER') ap.push(d);
      else dir.push(d);
    });
    setAuditors(ap);
    setDirectors(dir);
    notificationService.toast.success('Prior-year panel loaded. Review and Save.');
  }, [engagementId, loadCarryForward]);

  // ───── save ─────
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!auditFirmId) errors.auditFirmId = 'Audit firm is required';
    auditors.forEach((a, i) => {
      if (!a.signatoryMasterId) errors[`auditor-${a.key}`] = 'Partner is required';
      if (a.udinNumber && !UDIN_REGEX.test(a.udinNumber)) {
        errors[`udin-${a.key}`] = UDIN_FORMAT_MESSAGE;
      }
    });
    directors.forEach(d => {
      if (!d.signatoryMasterId) errors[`director-${d.key}`] = 'Signatory is required';
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [auditFirmId, auditors, directors]);

  const handleSaveClick = useCallback(() => {
    if (!validate()) return;
    setSaveConfirmOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const signatories: UpsertEngagementSignaturePanelSignatoryItem[] = [
        ...auditors.map((a, i) => ({
          role: 'AUDITOR_PARTNER' as const,
          signatoryMasterId: a.signatoryMasterId,
          signatoryMasterType: a.signatoryMasterType,
          displayOrder: i + 1,
          udinNumber: a.udinNumber.trim() || null,
        })),
        // Entity side. The row's OWN master type is sent, not the current
        // template's — so a panel carried forward before the template
        // changed still resolves against the master it was picked from.
        ...directors.map((d, i) => ({
          role: 'DIRECTOR' as const,
          signatoryMasterId: d.signatoryMasterId,
          signatoryMasterType: d.signatoryMasterType,
          columnPosition: i < 4 ? i + 1 : null,
          displayOrder: i + 1,
        })),
      ];
      await fsaEngagementSignaturePanelService.upsert({
        primaEngagementId: engagementId,
        auditFirmId,
        place: place.trim() || null,
        reportDate: reportDate || null,
        entityPlace: entityPlace.trim() || null,
        entityDate: entityDate || null,
        notes: notes.trim() || null,
        signatories,
        rowVersion,
        statusCode: 'ACTIVE',
        refreshFromMasters,
      });
      setRefreshFromMasters(false);
      notificationService.toast.success('Signature panel saved.');
      setIsSaving(false);
      refetch();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Save failed', 6000);
    }
  }, [engagementId, auditFirmId, auditors, directors, place, reportDate, entityPlace, entityDate, notes, rowVersion, refetch, refreshFromMasters]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading signature panel…
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 1.25rem', overflow: 'auto', flex: 1 }}>
      {/* ───── Header card ───── */}
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', gap: '0.5rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>Engagement Signature Panel</h3>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Picks the audit firm and signatories used on signed FS. Snapshots are taken at save time so master edits don't alter previously-signed engagements.
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleCarryForwardClick}
            disabled={cfLoading || isSaving}
            title="Load audit firm + signatories from the prior-year engagement"
            style={{ fontSize: '0.8125rem', padding: '0.5rem 0.875rem' }}
          >
            <IconSystem.refresh size={14} /> Carry Forward
          </button>
        </div>

        <div className="form-grid-3">
          <div className="form-group" data-field="auditFirmId">
            <SyutSelect
              label="Audit Firm"
              required
              value={auditFirmId}
              onChange={(val) => { setAuditFirmId((val as string) || ''); }}
              isAsync
              loadOptions={loadAuditFirmOptions}
              defaultOptions
              cacheOptions={false}
              seedOptions={auditFirmId && auditFirmLabel ? [{ value: auditFirmId, label: auditFirmLabel }] : undefined}
              error={fieldErrors.auditFirmId}
              placeholder="Select audit firm…"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Auditor's Place</label>
            <input
              type="text"
              className="form-input"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              maxLength={200}
              placeholder="e.g. Bengaluru"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Auditor's Report Date</label>
            <input
              type="date"
              className="form-input"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Internal Notes</label>
          <textarea
            className="form-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Auditor remarks about the signing (not printed)."
          />
        </div>
      </div>

      {/* ───── Auditor side ───── */}
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h4 style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>Auditor Side — Signing Partners</h4>
          <button className="btn btn-primary" onClick={addAuditor} disabled={!auditFirmId || isSaving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
            <IconSystem.add size={14} /> Add Partner
          </button>
        </div>
        {!auditFirmId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            Select an audit firm first to add partners.
          </div>
        )}
        {auditors.length === 0 && auditFirmId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            No partners added yet. Click <strong>Add Partner</strong> above.
          </div>
        )}
        {auditors.map((row, idx) => (
          <div key={row.key} style={{
            display: 'grid', gridTemplateColumns: '1fr 280px auto auto', gap: '0.5rem',
            alignItems: 'flex-end', padding: '0.5rem 0', borderTop: idx > 0 ? '1px solid var(--border-color)' : 'none',
          }}>
            <div className="form-group" style={{ marginBottom: 0 }} data-field={`auditor-${row.key}`}>
              <SyutSelect
                label={idx === 0 ? `Partner #${idx + 1}` : ''}
                value={row.signatoryMasterId}
                onChange={(val) => {
                  updateAuditor(row.key, {
                    signatoryMasterId: (val as string) || '',
                  });
                }}
                isAsync
                loadOptions={loadPartnerOptions}
                defaultOptions
                cacheOptions={false}
                seedOptions={row.signatoryMasterId && row.signatoryLabel
                  ? [{ value: row.signatoryMasterId, label: row.signatoryLabel }] : undefined}
                error={fieldErrors[`auditor-${row.key}`]}
                placeholder="Select partner…"
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }} data-field={`udin-${row.key}`}>
              <label className="form-label" style={{ fontSize: '0.75rem' }}>UDIN</label>
              <input
                type="text"
                className={`form-input${fieldErrors[`udin-${row.key}`] ? ' form-field-error' : ''}`}
                value={row.udinNumber}
                onChange={(e) => updateAuditor(row.key, { udinNumber: e.target.value.toUpperCase() })}
                placeholder={`e.g. ${UDIN_EXAMPLE}`}
                maxLength={18}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem', fontFamily: 'monospace' }}
              />
              {fieldErrors[`udin-${row.key}`] && (
                <div className="form-error-message" style={{ fontSize: '0.7rem' }}>{fieldErrors[`udin-${row.key}`]}</div>
              )}
            </div>
            <MoveButtons index={idx} count={auditors.length} onMove={moveAuditor} />
            <button
              onClick={() => removeAuditor(row.key)}
              title="Remove partner"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', padding: '0.375rem',
              }}
            >
              <IconSystem.trash size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* ───── Board side ───── */}
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h4 style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
            {ownerSideHeading}
          </h4>
          <button className="btn btn-primary" onClick={addDirector} disabled={!clientId || isSaving} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
            <IconSystem.add size={14} /> Add {boardNoun}
          </button>
        </div>
        <div className="form-grid-3" style={{ alignItems: 'flex-end', marginBottom: '0.5rem' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Place</label>
            <input
              type="text"
              className="form-input"
              value={entityPlace}
              onChange={(e) => setEntityPlace(e.target.value)}
              maxLength={200}
              placeholder="Not printed when blank"
              title={`Where the ${boardNoun.toLowerCase()}s sign. Left blank, no Place line prints on this side — the auditor's place is never used here.`}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-input"
              value={entityDate}
              onChange={(e) => setEntityDate(e.target.value)}
              title="Date of approval / signing on this side. Left blank, the auditor's report date prints."
            />
            {!entityDate && reportDate && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                Blank — prints the auditor's date
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setEntityPlace(place); setEntityDate(reportDate); }}
              disabled={isSaving || (!place && !reportDate)}
              title="Copy the auditor's place and report date to this side"
              style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
            >
              Same as auditor
            </button>
          </div>
        </div>
        {!clientId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            Client not resolved yet.
          </div>
        )}
        {directors.length === 0 && clientId && !isNonCorporate && rosterSigners.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '0.5rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
            <span>
              Add the {rosterSigners.length} director{rosterSigners.length === 1 ? '' : 's'} in office on {entitySignDate || 'today'}, in the
              Corporate Facts order ({rosterSigners.map(d => d.name).join(', ')}) — then remove anyone who does not sign.
            </span>
            <button className="btn btn-secondary" onClick={addFromCorporateFacts} disabled={isSaving}
              style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem', flexShrink: 0 }}>
              Add from Corporate Facts
            </button>
          </div>
        )}
        {directors.length === 0 && clientId && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.5rem' }}>
            {isProprietorship ? 'No proprietor added yet.' : `No ${boardNoun.toLowerCase()}s added yet.`} Click <strong>Add {boardNoun}</strong> above. The picker lists{' '}
            {isNonCorporate
              ? <>this client&rsquo;s records from <strong>FSA &rarr; Partners</strong> that are flagged as signatories</>
              : <>this client&rsquo;s records from <strong>FSA &rarr; Director Signatories</strong></>}
            , active as of {entitySignDate || 'today'}.
          </div>
        )}
        {directors.map((row, idx) => (
          <div key={row.key} style={{
            display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0.5rem',
            alignItems: 'flex-end', padding: '0.5rem 0', borderTop: idx > 0 ? '1px solid var(--border-color)' : 'none',
          }}>
            <div className="form-group" style={{ marginBottom: 0 }} data-field={`director-${row.key}`}>
              <SyutSelect
                label={idx === 0 ? `Signatory #${idx + 1}` : ''}
                value={row.signatoryMasterId}
                onChange={(val) => {
                  updateDirector(row.key, {
                    signatoryMasterId: (val as string) || '',
                  });
                }}
                isAsync
                loadOptions={loadOwnerOptions}
                defaultOptions
                cacheOptions={false}
                seedOptions={row.signatoryMasterId && row.signatoryLabel
                  ? [{ value: row.signatoryMasterId, label: row.signatoryLabel }] : undefined}
                error={fieldErrors[`director-${row.key}`]}
                placeholder="Select signatory…"
                isClearable={false}
                size="sm"
              />
            </div>
            <MoveButtons index={idx} count={directors.length} onMove={moveDirector} />
            <button
              onClick={() => removeDirector(row.key)}
              title="Remove signatory"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', padding: '0.375rem',
              }}
            >
              <IconSystem.trash size={16} />
            </button>
          </div>
        ))}
      </div>

      {/* ───── Save bar ───── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', padding: '0.5rem 0' }}>
        {panel && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}
            title="Signatories already on the panel keep the name, designation and DIN they were saved with, so a later master edit cannot change a signed year. Tick to copy the masters' current details on this save.">
            <input type="checkbox" checked={refreshFromMasters} onChange={e => setRefreshFromMasters(e.target.checked)} disabled={isSaving} />
            Refresh names from masters
          </label>
        )}
        <button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save Signature Panel'}
        </button>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={refreshFromMasters
          ? 'Save signature panel for this engagement? Every signatory’s name, designation and DIN / membership number will be copied again from the masters.'
          : 'Save signature panel for this engagement? Newly added signatories are copied from the masters; those already on the panel keep the details they were saved with.'}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={isSaving}
        loadingMessage="Saving…"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={carryForwardConfirmOpen}
        title="Carry Forward from Prior Year?"
        message="Load audit firm + signatories from the prior-year engagement panel. Places and dates on both sides will be cleared (engagement-time facts you enter fresh). Existing unsaved changes in this form will be replaced."
        confirmLabel="Load"
        cancelLabel="Cancel"
        variant="default"
        loading={cfLoading}
        loadingMessage="Loading…"
        onConfirm={handleCarryForwardConfirm}
        onCancel={() => setCarryForwardConfirmOpen(false)}
      />
    </div>
  );
};

const MoveButtons: React.FC<{ index: number; count: number; onMove: (index: number, delta: -1 | 1) => void }> = ({ index, count, onMove }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} title="Move up"
      style={{ background: 'transparent', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: 'var(--text-secondary)', padding: '0.375rem', opacity: index === 0 ? 0.35 : 1 }}>
      <IconSystem.chevronUp size={16} />
    </button>
    <button type="button" onClick={() => onMove(index, 1)} disabled={index === count - 1} title="Move down"
      style={{ background: 'transparent', border: 'none', cursor: index === count - 1 ? 'default' : 'pointer', color: 'var(--text-secondary)', padding: '0.375rem', opacity: index === count - 1 ? 0.35 : 1 }}>
      <IconSystem.chevronDown size={16} />
    </button>
  </div>
);

export default FsaSignaturePanelPane;
